import { Callback } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"

import { isMobile } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"

// The shape every toggle in the top-right HUD row shares: a small pill carrying a glyph and a
// one-word label, on a dark translucent plate with a thin light rim.
//
// It exists as one object because there are now three of these sitting in a row (players, items,
// map) and they have to read as one set of controls rather than three badges that happen to be
// near each other. The players chip is not built on it -- it carries a bespoke arrival flash that
// repaints the plate -- but it is drawn from the same constants, which live here so the three
// cannot drift apart.
export const CHIP_PAD_X = 6
export const CHIP_PAD_Y = 4
const CORNER = 5
const GLYPH_SIZE = 11
const GAP = 4

export const CHIP_PANEL_COLOR = 0x05070a
export const CHIP_PANEL_ALPHA = 0.59
export const CHIP_PANEL_ALPHA_HOVER = 0.82
export const CHIP_BORDER_ALPHA = 0.36
export const CHIP_BORDER_ALPHA_HOVER = 0.75
export const CHIP_LABEL_COLOR = 0xcfd8dc
export const CHIP_LABEL_COLOR_HOVER = 0xffffff

// How far proud of the plate the tap/click target reaches. These chips are small on any screen and
// genuinely small under a thumb, and each is the only way to whatever it opens, so the target is
// grown rather than the pill -- a bigger pill would eat the corner the map itself wants.
const HIT_PAD = isMobile() ? 10 : 3

export interface HudChipProps {
    glyph: string
    label: string
    onClick: Callback
}

export class HudChip extends Container {
    protected plate = new Graphics()
    private glyph: Text
    private label: Text

    constructor({ glyph, label, onClick }: HudChipProps) {
        super()

        // No tint on the emoji -- it carries its own colours.
        this.glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(glyph) as Text
        this.label = new TextRenderer().size("tiny").render(label) as Text

        this.glyph.x = CHIP_PAD_X
        this.label.x = CHIP_PAD_X + this.glyph.width + GAP
        // Both sit on a shared centre line, so the glyph and the text don't stagger.
        const h = Math.max(this.glyph.height, this.label.height)
        this.glyph.y = CHIP_PAD_Y + (h - this.glyph.height) / 2
        this.label.y = CHIP_PAD_Y + (h - this.label.height) / 2

        this.addChild(this.plate, this.glyph, this.label)
        this.draw(false)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(
            -HIT_PAD,
            -HIT_PAD,
            this.getWidth() + HIT_PAD * 2,
            this.getHeight() + HIT_PAD * 2,
        )
        this.on("mouseover", () => this.draw(true))
        this.on("mouseout", () => this.draw(false))
        this.on("click", onClick)
        this.on("tap", onClick)

        // Every chip in this row is put on screen by whatever owns it, once it has something to
        // say. None of them should appear merely by being constructed.
        this.visible = false
    }

    protected draw = (hover: boolean) => {
        this.plate.clear()
        this.plate.beginFill(CHIP_PANEL_COLOR, hover ? CHIP_PANEL_ALPHA_HOVER : CHIP_PANEL_ALPHA)
        this.plate.lineStyle(1, 0xffffff, hover ? CHIP_BORDER_ALPHA_HOVER : CHIP_BORDER_ALPHA)
        this.plate.drawRoundedRect(0, 0, this.getWidth(), this.getHeight(), CORNER)
        this.plate.endFill()
        this.label.tint = hover ? CHIP_LABEL_COLOR_HOVER : CHIP_LABEL_COLOR
    }

    // Measured off the content rather than getBounds(), so the plate can be drawn to the size the
    // caller is about to position against without a chicken-and-egg on bounds.
    getWidth = (): number => CHIP_PAD_X * 2 + this.glyph.width + GAP + this.label.width
    getHeight = (): number => CHIP_PAD_Y * 2 + Math.max(this.glyph.height, this.label.height)
}
